import React, { useState, useEffect } from 'react';
import { useSelector } from "react-redux";
import Loading from "../../component/Loading/Loading";
import AlertMsg from '../../component/AlertMsg/AlertMsg';
import { AiOutlineSortAscending } from "react-icons/ai";
import Pagination from '../../utils/Pagenation/Pagenation';
import useAxiosInstance from '../../services/useAxiosInstance';
import { Plus, Pencil, Search } from "lucide-react";
import GlassmorphicModal from '../../component/ReusableComponents/GlassmorphicModal';

function AddViolation() {
    const axiosInstance = useAxiosInstance();
    const currentDate = new Date();
    const formattedDates = currentDate.toISOString();
    const communityId = useSelector((state) => state.auth.communityId);
    const userId = useSelector((state) => state.auth.userId);
    const [violations, setViolations] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [editMode, setEditMode] = useState(false);
    const [violationTypeId, setViolationTypeId] = useState(null);
    const [alert, setAlert] = useState({ visible: false, message: '', type: '' });

    const [formData, setFormData] = useState({
        activeStatus: true,
        amount: 0,
        communityId: communityId,
        createdById: userId,
        createdDate: formattedDates,
        description: "",
        lateCharge: 0,
        modifiedById: userId,
        modifiedDate: formattedDates,
        name: "",
        violationTypeId: 0
    });

    useEffect(() => {
        async function fetchViolations() {
            setLoading(true);
            try {
                const response = await axiosInstance.get(
                    `/api/violationtype/findByCommunityId/${communityId}`
                );
                setViolations(response.data);
            } catch (error) {
                console.error('Error fetching violations:', error);
            } finally {
                setLoading(false);
            }
        }
        if (communityId && userId) {
            fetchViolations();
        }
    }, [communityId, userId]);

    const handleAddViolation = () => {
        setEditMode(false);
        setShowModal(true);
        resetFormData();
    };

    const handleCloseModal = () => {
        setShowModal(false);
        resetFormData();
        setErrors({});
    };

    const resetFormData = () => {
        setFormData({
            activeStatus: true,
            amount: "",
            communityId: communityId,
            createdById: userId,
            createdDate: formattedDates,
            description: "",
            lateCharge: "",
            modifiedById: userId,
            modifiedDate: formattedDates,
            name: "",
            violationTypeId: 0
        });
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        const parsedValue = type === 'checkbox' ? checked : ["lateCharge", "amount"].includes(name) ? parseInt(value, 10) : value;
        setFormData({ ...formData, [name]: parsedValue });

        if (["lateCharge", "amount"].includes(name)) {
            validateField(name, parsedValue);
        }
    };

    const handleRadioChange = (e) => {
        const { value } = e.target;
        setFormData({ ...formData, activeStatus: value === 'active' });
    };

    const validateField = (fieldName, value) => {
        let errorMessage = "";
        setErrors({ ...errors, [fieldName]: errorMessage });
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.name) {
            newErrors.name = "Violation Name is required.";
        }
        if (!formData.description) {
            newErrors.description = "Description is required.";
        }
        if (!formData.amount) {
            newErrors.amount = "Amount is required.";
        }

        if (!formData.lateCharge) {
            newErrors.lateCharge = "Late Charge is required.";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        if (!validate()) {
            setLoading(false);
            return;
        }

        try {
            if (editMode) {
                await axiosInstance.put(`/api/violationtype/update/${violationTypeId}`, formData);
                const updatedViolations = violations.map(violation =>
                    violation.violationTypeId === violationTypeId ? { ...violation, ...formData } : violation
                );
                setViolations(updatedViolations);
                setAlert({ visible: true, message: 'Violation updated successfully!', type: 'success' });
            } else {
                const response = await axiosInstance.post(`/api/violationtype/add`, formData);
                setViolations([...violations, response.data]);
                setAlert({ visible: true, message: 'Violation added successfully!', type: 'success' });
            }

            setShowModal(false);
            resetFormData();
            setErrors({});
        } catch (error) {
            console.error('Error details:', error.response || error);
            if (error.response && error.response.status === 409) {
                setAlert({
                    visible: true,
                    message: 'Violation type already exists or there is a conflict. Please check your data.',
                    type: 'error'
                });
            } else if (error.response && error.response.status === 400) {
                setAlert({
                    visible: true,
                    message: 'Bad Request. Please check the data you are submitting.',
                    type: 'error'
                });
            } else {
                setAlert({
                    visible: true,
                    message: 'Error submitting violation. Please try again.',
                    type: 'error'
                });
            }
            console.error('Error submitting violation:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEditViolation = (violation) => {
        setEditMode(true);
        setViolationTypeId(violation.violationTypeId);
        setFormData({ ...violation });
        setShowModal(true);
    };

    const [currentPage, setCurrentPage] = useState(0);
    const itemsPerPage = 5;
    const [sortOrder, setSortOrder] = useState('none');

    const handleSort = () => {
        setSortOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'));
    };

    const sortedViolations = [...violations].sort((a, b) => {
        if (sortOrder === 'asc') {
            return a.name.localeCompare(b.name);
        } else if (sortOrder === 'desc') {
            return b.name.localeCompare(a.name);
        }
        return 0;
    });

    const pageCount = Math.ceil(violations.length / itemsPerPage);
    const currentViolations = sortedViolations.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);

    // Modal footer components
    const modalFooter = (
        <div className="flex justify-end space-x-2">
            <button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                onClick={handleCloseModal}
            >
                Cancel
            </button>
            <button
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                onClick={handleSubmit}
            >
                Submit
            </button>
        </div>
    );

    return (
        <div className="bg-gray-50 min-h-screen md:p-4">
            {loading && <Loading />}
            {!loading && (
                <div className="max-w-7xl mx-auto">
                    {alert.visible && (
                        <AlertMsg
                            message={alert.message}
                            type={alert.type}
                            onClose={() => setAlert({ ...alert, visible: false })}
                        />
                    )}
                    <div className="flex justify-between items-center mb-6">
                        <h1 className="text-2xl font-bold text-gray-800">Violation Types</h1>
                        <div className="flex items-center">
                            <button
                                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md flex items-center gap-2"
                                onClick={handleAddViolation}
                            >
                                <Plus size={18} />
                                <span>Add</span>
                            </button>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-blue-400">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">Name
                                            <button
                                                onClick={handleSort}
                                            >
                                                <AiOutlineSortAscending className="inline ml-1" />
                                            </button>
                                        </th>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider max-w-[500px] whitespace-break-spaces">Description</th>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">Amount</th>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">Late Charge</th>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">Status</th>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {currentViolations.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">No Data Found</td>
                                        </tr>
                                    ) : (
                                        currentViolations.map((violation) => (
                                            <tr key={violation.violationTypeId} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-left">{violation.name}</td>
                                                <td className="px-6 py-4 text-sm text-gray-900  max-w-[400px] whitespace-break-spaces  text-left">{violation.description}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-left">{violation.amount}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-left">{violation.lateCharge}</td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${violation.activeStatus
                                                        ? "bg-green-100 text-green-800"
                                                        : "bg-red-100 text-red-800"
                                                        }`}>
                                                        {violation.activeStatus ? "Active" : "Inactive"}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                    <button
                                                        className="text-gray-500 hover:text-gray-700"
                                                        onClick={() => handleEditViolation(violation)}
                                                        title="Edit Violation"
                                                    >
                                                        <Pencil size={18} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t border-gray-200">
                            <div className="text-sm text-gray-700">
                                Page {currentPage + 1} of {pageCount}
                            </div>
                            <div className="flex-1 flex justify-end">
                                <Pagination
                                    currentPage={currentPage}
                                    pageCount={pageCount}
                                    setCurrentPage={setCurrentPage}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add/Edit Modal using GlassmorphicModal */}
            <GlassmorphicModal
                isOpen={showModal}
                onClose={handleCloseModal}
                title={editMode ? "Edit Violation" : "Add Violation"}
                size="lg"
                footer={modalFooter}
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            maxLength={100}
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="Violation Name"
                            className={`w-full rounded-md border ${errors.name ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        />
                        {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            maxLength={100}
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            placeholder="Description"
                            className={`w-full rounded-md border ${errors.description ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        />
                        {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Amount <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="number"
                            maxLength={10}
                            name="amount"
                            value={formData.amount}
                            onChange={handleInputChange}
                            placeholder="Amount"
                            className={`w-full rounded-md border ${errors.amount ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        />
                        {errors.amount && <p className="text-red-500 text-sm mt-1">{errors.amount}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Late Charge <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="number"
                            maxLength={10}
                            name="lateCharge"
                            value={formData.lateCharge}
                            onChange={handleInputChange}
                            placeholder="Late Charge"
                            className={`w-full rounded-md border ${errors.lateCharge ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        />
                        {errors.lateCharge && <p className="text-red-500 text-sm mt-1">{errors.lateCharge}</p>}
                    </div>

                    {editMode && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Active Status</label>
                            <div className="flex gap-4">
                                <label className="inline-flex items-center cursor-pointer">
                                    <input
                                        type="radio"
                                        name="activeStatus"
                                        value="active"
                                        checked={formData.activeStatus}
                                        onChange={handleRadioChange}
                                        className="form-radio h-4 w-4 text-blue-500"
                                    />
                                    <span className="ml-2">Active</span>
                                </label>
                                <label className="inline-flex items-center cursor-pointer">
                                    <input
                                        type="radio"
                                        name="activeStatus"
                                        value="inactive"
                                        checked={!formData.activeStatus}
                                        onChange={handleRadioChange}
                                        className="form-radio h-4 w-4 text-blue-500"
                                    />
                                    <span className="ml-2">Inactive</span>
                                </label>
                            </div>
                        </div>
                    )}
                </div>

                <div className="text-sm text-red-500 mt-4">* indicates required field</div>
            </GlassmorphicModal>
        </div>
    );
}

export default AddViolation;